"use client";

import { Row, Col, Form } from "react-bootstrap";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDataFetch } from "../../../hooks/use-data-fetch";

type ResetFormData = {
  password: string;
  confirmPassword: string;
};

const ResetPasswordForm = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const { getData, postData } = useDataFetch();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetFormData>();

  const {
    mutateAsync,
    isPending,
    error: postError,
    isSuccess,
  } = postData("users/update/password");

  const {  error, isLoading } = getData(
    ["confirm-token", token],
    `users/confirm/token/${token}`,
    { enabled: !!token }
  );

  useEffect(() => {
    if (error) {
      console.error("Token inválido:", error.message);
    }
  }, [error]);

  useEffect(() => {
    if (isSuccess) {
      const timeout = setTimeout(() => navigate("/login"), 1500);
      return () => clearTimeout(timeout);
    }
  }, [isSuccess, navigate]);

  const onSubmit = async (formData: ResetFormData) => {
    try {
      await mutateAsync({
        password: formData.password,
        token,
      });
    } catch (e) {
      console.error("Error al cambiar la contraseña", e);
    }
  };

  return (
    <div className="auth-main-content m-auto m-1230 px-3">
      <Row className="align-items-center">
        <Col lg={6} className="d-none d-lg-block">
          <img
            src="/images/login_mth.jpg"
            className="rounded-3"
            alt="reset"
            width={646}
            height={804}
          />
        </Col>

        <Col lg={6}>
          <div className="mw-480 ms-lg-auto">
            <div className="d-inline-block mb-4">
              <img
                src="/images/logo.svg"
                className="rounded-3 for-light-logo"
                alt="login"
                width={100}
                height={26}
              />
              <img
                src="/images/logo.svg"
                className="rounded-3 for-dark-logo"
                alt="login"
                width={100}
                height={26}
              />
            </div>

            <h3 className="fs-28 mb-2">¿Restablecer contraseña?</h3>
            <p className="fw-medium fs-16 mb-4">
              Ingresa tu nueva contraseña y confírmala a continuación.
            </p>

            {isLoading ? (
              <p>Cargando...</p>
            ) : error ? (
              <p className="text-danger">Token inválido o expirado</p>
            ) : isSuccess ? (
              <p className="text-success">
                ¡Contraseña actualizada correctamente! Redirigiendo...
              </p>
            ) : (
              <Form onSubmit={handleSubmit(onSubmit)}>
                <Form.Group className="mb-4">
                  <label className="label text-secondary">
                    Nueva contraseña
                  </label>
                  <Form.Control
                    type="password"
                    className="h-55"
                    placeholder="Escribe tu nueva contraseña"
                    {...register("password", {
                      required: "Este campo es obligatorio",
                      minLength: {
                        value: 10,
                        message: "Debe tener al menos 10 caracteres",
                      },
                    })}
                  />
                  {errors.password && (
                    <p className="text-danger mt-1">
                      {errors.password.message}
                    </p>
                  )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <label className="label text-secondary">
                    Confirmar contraseña
                  </label>
                  <Form.Control
                    type="password"
                    className="h-55"
                    placeholder="Confirma tu nueva contraseña"
                    {...register("confirmPassword", {
                      required: "Este campo es obligatorio",
                      validate: (val) =>
                        val === watch("password") ||
                        "Las contraseñas no coinciden",
                    })}
                  />
                  {errors.confirmPassword && (
                    <p className="text-danger mt-1">
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </Form.Group>

                <Form.Group className="mb-4">
                  <button
                    type="submit"
                    className="btn btn-primary fw-medium py-2 px-3 w-100"
                    disabled={isSubmitting || isPending}
                  >
                    <div className="d-flex align-items-center justify-content-center py-1">
                      <span className="material-symbols-outlined fs-20 text-white me-2">
                        autorenew
                      </span>
                      <span>
                        {isSubmitting || isPending ? "Enviando..." : "Enviar"}
                      </span>
                    </div>
                  </button>
                </Form.Group>

                {postError && (
                  <p className="text-danger">{postError.message}</p>
                )}

                <Form.Group>
                  <p>
                    Volver al{" "}
                    <Link
                      to="/authentication/sign-in"
                      className="fw-medium text-primary text-decoration-none"
                    >
                      inicio de sesión
                    </Link>
                  </p>
                </Form.Group>
              </Form>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default ResetPasswordForm;
